import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Star,
  Package,
  CheckCircle,
  Link as PhosphorLink,
  ShieldCheck,
  MagnifyingGlass,
  Download,
} from '@phosphor-icons/react';
import {
  marketplaceApi,
  MarketplaceSupplier,
  CatalogProduct,
} from '@dukene/shared/api/marketplace';
import { useAuthStore, useNotificationStore } from '@dukene/shared';

export default function MarketplaceSupplierDetail() {
  const { supplierId } = useParams<{ supplierId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [supplier, setSupplier] = useState<MarketplaceSupplier | null>(null);
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showInStockOnly, setShowInStockOnly] = useState(false);
  const [markupType, setMarkupType] = useState<'PERCENTAGE' | 'FIXED'>('PERCENTAGE');
  const [markupValue, setMarkupValue] = useState(50);

  useEffect(() => {
    if (supplierId) {
      loadSupplier();
      loadCatalog();
    }
  }, [supplierId]);

  const loadSupplier = async () => {
    const { addNotification } = useNotificationStore.getState();
    try {
      const [supplierData, connectedData] = await Promise.all([
        marketplaceApi.getSupplier(supplierId!),
        user?.storeId
          ? marketplaceApi.getConnectedSuppliers(user.storeId)
          : Promise.resolve([]),
      ]);

      setSupplier(supplierData);

      const connections = Array.isArray(connectedData)
        ? connectedData
        : (connectedData as any)?.connections || [];
      setIsConnected(connections.some((c: any) => c.supplierId === supplierId));
    } catch (error: any) {
      addNotification({
        type: 'error',
        message: error.message || 'Failed to load supplier',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadCatalog = async () => {
    if (!supplierId) return;
    try {
      setProductsLoading(true);
      const { products: data } = await marketplaceApi.getSupplierCatalog(supplierId, {
        search: searchQuery || undefined,
        inStock: showInStockOnly || undefined,
      });
      setProducts(data || []);
    } catch (error: any) {
      console.error('Failed to load catalog:', error);
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => {
    if (supplierId) {
      loadCatalog();
    }
  }, [searchQuery, showInStockOnly]);

  const handleConnect = async () => {
    const { addNotification } = useNotificationStore.getState();
    if (!user?.storeId || !supplier) return;

    try {
      const result = await marketplaceApi.connectSupplier(user.storeId, {
        supplierId: supplier.id,
        markupType,
        markupValue,
        autoSync: true,
      });

      if (result.requiresOAuth && result.authorizationUrl) {
        const authUrl = new URL(result.authorizationUrl);
        const { token } = useAuthStore.getState();
        if (token) {
          authUrl.searchParams.set('token', token);
        }
        addNotification({
          type: 'success',
          message: 'Redirecting to OAuth authorization...',
        });
        window.location.href = authUrl.toString();
      } else {
        setIsConnected(true);
        addNotification({ type: 'success', message: `Connected to ${supplier.name}!` });
      }
    } catch (error: any) {
      addNotification({
        type: 'error',
        message: error.message || 'Failed to connect supplier',
      });
    }
  };

  const toggleSelectProduct = (id: string) => {
    const next = new Set(selectedProducts);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedProducts(next);
  };

  const toggleSelectAll = () => {
    if (selectedProducts.size === products.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(products.map((p) => p.id)));
    }
  };

  const handleImportProducts = async () => {
    if (!user?.storeId || !supplierId || selectedProducts.size === 0) return;
    const { addNotification } = useNotificationStore.getState();

    try {
      setImporting(true);
      await marketplaceApi.importProducts(user.storeId, {
        supplierId,
        productIds: Array.from(selectedProducts),
        markupType,
        markupValue,
      } as any);
      addNotification({
        type: 'success',
        message: `Successfully imported ${selectedProducts.size} products to your catalog!`,
      });
      setSelectedProducts(new Set());
    } catch (error: any) {
      addNotification({
        type: 'error',
        message: error.message || 'Failed to import selected products',
      });
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-12 space-y-4 animate-pulse">
        <div className="h-10 bg-[#F6F6F7] dark:bg-slate-900 rounded-xl" />
        <div className="h-44 bg-[#F6F6F7] dark:bg-slate-900 rounded-xl" />
      </div>
    );
  }

  if (!supplier) {
    return (
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-12 text-center">
        <h3 className="text-sm font-bold text-[#202223] dark:text-white">Supplier Not Found</h3>
        <button
          type="button"
          onClick={() => navigate('/suppliers/marketplace')}
          className="mt-4 h-8 px-4 bg-[#006838] text-white text-xs font-semibold rounded-lg"
        >
          Return to Marketplace
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Polaris High-Density Header */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/suppliers/marketplace')}
            className="p-1.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-[#5C5F62] transition-colors"
          >
            <ArrowLeft weight="bold" className="w-4 h-4" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-base font-bold text-[#202223] dark:text-white">
                {supplier.name}
              </h1>
              {supplier.isVerified && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-[#EBF5FA] text-[#006FBB]">
                  <ShieldCheck weight="fill" className="w-3 h-3" />
                  Verified
                </span>
              )}
            </div>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
              {supplier.categories?.[0] || 'Wholesale Distributor'} •{' '}
              <span className="inline-flex items-center gap-0.5 text-amber-500 font-bold">
                <Star weight="fill" className="w-3 h-3" />
                {supplier.rating || '4.9'}
              </span>
            </p>
          </div>
        </div>

        <div>
          {isConnected ? (
            <span className="h-8 px-3.5 rounded-lg bg-[#E3F1DF] text-[#006838] text-xs font-bold inline-flex items-center gap-1.5">
              <CheckCircle weight="fill" className="w-4 h-4" />
              Connected Partner
            </span>
          ) : (
            <button
              type="button"
              onClick={handleConnect}
              className="h-8 px-4 bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold rounded-lg inline-flex items-center gap-1.5 shadow-xs transition-colors"
            >
              <PhosphorLink weight="bold" className="w-3.5 h-3.5" />
              Connect & Sync Catalog
            </button>
          )}
        </div>
      </div>

      {/* Pricing Markup Config Card */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xs font-bold text-[#202223] dark:text-white uppercase tracking-wider">
            Automated Pricing Rules
          </h2>
          <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
            Configure your store retail price markup when importing products from this catalog
          </p>
        </div>

        <div className="flex items-center gap-2">
          <select
            value={markupType}
            onChange={(e) => setMarkupType(e.target.value as any)}
            className="h-8 px-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-semibold text-[#202223] dark:text-white"
          >
            <option value="PERCENTAGE">Percentage Markup (%)</option>
            <option value="FIXED">Fixed Amount (+KES)</option>
          </select>

          <input
            type="number"
            value={markupValue}
            onChange={(e) => setMarkupValue(Number(e.target.value))}
            className="h-8 w-20 px-2 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-right font-mono font-bold text-[#202223] dark:text-white"
          />

          <span className="text-xs font-bold text-[#5C5F62]">
            {markupType === 'PERCENTAGE' ? '%' : 'KES'}
          </span>
        </div>
      </div>

      {/* Catalog Directory */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl shadow-sm overflow-hidden">
        <div className="p-3.5 border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/50 dark:bg-slate-900/40 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlass
                weight="bold"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5F62]"
              />
              <input
                type="text"
                placeholder="Search catalog products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-8 pl-9 pr-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white placeholder-[#8C9196] focus:outline-none focus:ring-1 focus:ring-[#006838]"
              />
            </div>

            <label className="inline-flex items-center gap-1.5 text-xs text-[#5C5F62] cursor-pointer shrink-0">
              <input
                type="checkbox"
                checked={showInStockOnly}
                onChange={(e) => setShowInStockOnly(e.target.checked)}
                className="rounded border-[#D2D5D9] text-[#006838]"
              />
              <span>In Stock Only</span>
            </label>
          </div>

          {selectedProducts.size > 0 && (
            <button
              type="button"
              onClick={handleImportProducts}
              disabled={importing}
              className="h-8 px-4 bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold rounded-lg inline-flex items-center gap-1.5 shadow-xs shrink-0"
            >
              <Download weight="bold" className="w-3.5 h-3.5" />
              {importing
                ? 'Importing...'
                : `Import ${selectedProducts.size} Selected Products`}
            </button>
          )}
        </div>

        {/* Catalog Table */}
        {productsLoading ? (
          <div className="p-12 space-y-3 animate-pulse">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-10 bg-[#F6F6F7] dark:bg-slate-900 rounded-lg" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16 px-6">
            <Package weight="fill" className="w-10 h-10 text-[#8C9196] mx-auto mb-3" />
            <h3 className="text-sm font-semibold text-[#202223] dark:text-white">
              No Catalog Items Found
            </h3>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-1">
              Try adjusting your search query or removing filters.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/60 dark:bg-slate-900/40 text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] dark:text-slate-400">
                  <th className="py-2.5 px-4 w-10">
                    <input
                      type="checkbox"
                      checked={
                        products.length > 0 && selectedProducts.size === products.length
                      }
                      onChange={toggleSelectAll}
                      className="rounded border-[#D2D5D9] text-[#006838]"
                    />
                  </th>
                  <th className="py-2.5 px-4">Product Name</th>
                  <th className="py-2.5 px-4">SKU</th>
                  <th className="py-2.5 px-4 text-right">Wholesale Cost</th>
                  <th className="py-2.5 px-4 text-right">Suggested Retail</th>
                  <th className="py-2.5 px-4 text-right">Available Stock</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E1E4E8] dark:divide-slate-800 text-xs">
                {products.map((product) => {
                  const isSelected = selectedProducts.has(product.id);
                  const cost = Number(product.wholesalePrice || 0);
                  const retailPrice =
                    markupType === 'PERCENTAGE'
                      ? cost * (1 + markupValue / 100)
                      : cost + markupValue;

                  return (
                    <tr
                      key={product.id}
                      className={`hover:bg-[#F6F6F7]/60 dark:hover:bg-slate-800/40 transition-colors ${
                        isSelected ? 'bg-[#E3F1DF]/30 dark:bg-[#006838]/10' : ''
                      }`}
                    >
                      <td className="py-3 px-4">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelectProduct(product.id)}
                          className="rounded border-[#D2D5D9] text-[#006838]"
                        />
                      </td>

                      <td className="py-3 px-4 font-semibold text-[#202223] dark:text-white">
                        {product.title}
                      </td>

                      <td className="py-3 px-4 font-mono text-[#5C5F62]">
                        {product.sku || '—'}
                      </td>

                      <td className="py-3 px-4 text-right font-mono text-[#5C5F62]">
                        KES {cost.toLocaleString()}
                      </td>

                      <td className="py-3 px-4 text-right font-mono font-bold text-[#006838]">
                        KES {Math.round(retailPrice).toLocaleString()}
                      </td>

                      <td className="py-3 px-4 text-right font-mono font-bold text-[#202223] dark:text-white">
                        {(product as any).inventoryQuantity ?? (product as any).stock ?? 100}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
